/**
 * WizardShell — new project wizard content (rendered inside V3Shell).
 * canonical: S1 lines 722–754
 *
 * - Renders inside V3Shell — left sidebar (Dashboard / Projects / Subs / Settings) stays visible
 * - Topbar: breadcrumb + Cancel + Save & exit
 * - .wiz-rail: 5 steps with active/done state; connector lines
 * - URL ?step=1..5 drives which step renders
 * - SorenHome is mounted at App root and persists automatically (soren-orb)
 * - sessionStorage v4_wizard_in_progress synced via useWizardState
 */

import { Suspense, lazy } from 'react'
import { Link } from 'react-router-dom'
import { useWizardState } from './hooks/useWizardState'

// Lazy-load each step to keep initial bundle lean
const Step1ProjectInfo = lazy(() => import('./steps/Step1ProjectInfo'))
const Step2UploadSOV   = lazy(() => import('./steps/Step2UploadSOV'))
const Step3ReviewSOV   = lazy(() => import('./steps/Step3ReviewSOV'))
const Step4Sub0Period   = lazy(() => import('./steps/Step4PayApp/Sub0PeriodInfo'))
const Step4Sub1G703    = lazy(() => import('./steps/Step4PayApp/Sub1G703Billing'))
const Step5PreLien     = lazy(() => import('./steps/Step5PreLien'))

const STEP_LABELS = ['Project info', 'Upload SOV', 'Review SOV', 'Pay App #1', 'Pre Lien']

export default function WizardShell() {
  const { currentStep, wizard, setWizard, goToStep, cancelWizard, saveAndExit, flushDraftSave, completeWizard } =
    useWizardState()

  return (
    <div data-testid="wizard-shell">
      {/* ── Wizard header ── */}
      <div
        className="topbar"
        style={{ marginBottom: 16, background: 'transparent', position: 'static', padding: 0 }}
      >
        <div className="crumb">
          <Link to="/dashboard" style={{ color: 'inherit', textDecoration: 'none' }}>← Dashboard</Link>
          {' · '}
          <Link to="/projects" style={{ color: 'inherit', textDecoration: 'none' }}>Projects</Link>
          {' · '}
          <b>New project</b>
        </div>
        <div className="topbar-r">
          <button
            className="btn b-gh btn-sm"
            onClick={cancelWizard}
            data-testid="wizard-cancel"
          >
            ✕ Cancel
          </button>
          <button
            className="btn b-ol btn-sm"
            onClick={saveAndExit}
            data-testid="wizard-save-exit"
          >
            Save &amp; exit
          </button>
        </div>
      </div>

      {/* ── Wizard rail (canonical L744–751) ── */}
      <div className="wiz-rail" data-testid="wizard-rail">
        {STEP_LABELS.map((label, i) => {
          const stepNum = i + 1
          const isActive = stepNum === currentStep
          const isDone   = stepNum < currentStep
          return (
            <div key={stepNum} style={{ display: 'flex', alignItems: 'center' }}>
              <div
                className={`ws${isActive ? ' active' : ''}${isDone ? ' done' : ''}`}
                onClick={() => isDone ? goToStep(stepNum) : undefined}
                style={{
                  cursor: isDone ? 'pointer' : isActive ? 'default' : 'not-allowed',
                  opacity: !isDone && !isActive ? 0.45 : 1,
                }}
                data-testid={`wizard-step-${stepNum}`}
                aria-current={isActive ? 'step' : undefined}
              >
                <div className="ws-n">{isDone ? '✓' : stepNum}</div>
                <div className="ws-l">{label}</div>
              </div>
              {i < STEP_LABELS.length - 1 && (
                <div className={`ws-line${isDone ? ' done' : ''}`} />
              )}
            </div>
          )
        })}
      </div>

      {/* ── Step content ── */}
      <div>
        <Suspense
          fallback={
            <div className="wstep" style={{ color: 'var(--ink4)', fontSize: 13 }}>
              Loading…
            </div>
          }
        >
          {currentStep === 1 && (
            <Step1ProjectInfo
              wizard={wizard}
              setWizard={setWizard}
              onNext={() => goToStep(2)}
            />
          )}
          {currentStep === 2 && (
            <Step2UploadSOV
              wizard={wizard}
              setWizard={setWizard}
              onBack={() => goToStep(1)}
              onNext={() => goToStep(3)}
            />
          )}
          {currentStep === 3 && (
            <Step3ReviewSOV
              wizard={wizard}
              setWizard={setWizard}
              onBack={() => goToStep(2)}
              onNext={() => goToStep(4)}
            />
          )}
          {currentStep === 4 && (
            wizard.sub === 1 ? (
              <Step4Sub1G703
                wizard={wizard}
                setWizard={setWizard}
                onBack={() => {
                  flushDraftSave()
                  setWizard(prev => ({ ...prev, sub: 0 }))
                }}
                onNext={() => goToStep(5)}
              />
            ) : (
              <Step4Sub0Period
                wizard={wizard}
                setWizard={setWizard}
                onBack={() => goToStep(3)}
                onNext={() => setWizard(prev => ({ ...prev, sub: 1 }))}
              />
            )
          )}
          {currentStep === 5 && (
            <Step5PreLien
              wizard={wizard}
              setWizard={setWizard}
              onBack={() => goToStep(4)}
              onComplete={completeWizard}
            />
          )}
        </Suspense>
      </div>
    </div>
  )
}
